import { gql } from '@apollo/react-hooks';
import { FormItem } from '../../../common/components/Form';
import { getUser, getUserList } from '#/graphql/queries';
import { states } from '../mappings/mappings';
import { WorkOrderItem } from '#/graphql/types';

export const getEnhancedMenuItems = (menuItems: MenuItem[], selectedItems: WorkOrderItem[]) => {
  return menuItems.map((item) => {
    const rule = ACTION_STATE_RULES[item.id];
    if (!rule) return item;

    const eligible = rule
      ? selectedItems.filter((wo) => rule(wo?.state ?? ''))?.length
      : selectedItems?.length;

    return {
      ...item,
      isEligible: eligible > 0,
    };
  });
};

export const determineNextState = (selectedOptionId: string) => {
  let state: 'in_progress' | 'pending_review' | 'closed' | null = null;

  if (selectedOptionId === 'move_to_in_progress') state = 'in_progress';
  else if (selectedOptionId === 'move_to_pending_review') state = 'pending_review';
  else if (selectedOptionId === 'close_work_orders') state = 'closed';

  return state;
};

export const getNotEligibleContent = (selectedOptionId: string) => {
  let title = null;
  let message = null;

  if (!selectedOptionId) {
    return { title, message };
  }

  if (selectedOptionId === 'move_to_in_progress') {
    title = 'Move to In Progress';
    message =
      'No selected work orders match the criteria for this action. To move a work order to "In Progress" it has to be in state "Open".';
  } else if (selectedOptionId === 'move_to_pending_review') {
    title = 'Move to Pending Review';
    message =
      'No selected work orders match the criteria for this action. To move a work order to "Pending Review" it has to be in state "In Progress".';
  } else if (selectedOptionId === 'close_work_orders') {
    title = 'Close Work Orders';
    message =
      'No selected work orders match the criteria for this action. To close work order it should be in state "Pending Review".';
  } else if (selectedOptionId === 'assign_to_me') {
    title = 'Assign to Me';
    message =
      'No selected work orders match the criteria for this action. To assign a work order to yourself it has to not be in state "Closed".';
  } else if (selectedOptionId === 'bulk_assign') {
    title = 'Bulk Assign';
    message =
      'No selected work orders match the criteria for this action. To bulk assign work orders they have to not be in state "Closed".';
  }

  return { title, message };
};

export const getSuccessMessage = (selectedOptionId: string, updatedCount: number) => {
  if (!selectedOptionId || updatedCount <= 0) {
    return 'No work orders were updated.';
  }

  let successMessage = `${updatedCount} work orders were successfully `;

  if (selectedOptionId === 'move_to_in_progress') {
    successMessage += `moved to "In Progress".`;
  } else if (selectedOptionId === 'move_to_pending_review') {
    successMessage += `moved to "Pending Review".`;
  } else if (selectedOptionId === 'close_work_orders') {
    successMessage += `closed.`;
  } else if (selectedOptionId === 'assign_to_me') {
    successMessage += `assigned to you.`;
  } else {
    successMessage += `updated.`;
  }

  return successMessage;
};

export const ACTION_STATE_RULES: Record<string, (state: string) => boolean> = {
  assign_to_me: (state) => state !== states.closed.key,
  bulk_assign: (state) => state !== states.closed.key,
  move_to_in_progress: (state) => state === states.open.key,
  move_to_pending_review: (state) => state === states.in_progress.key,
  close_work_orders: (state) => state === states.pending_review.key,
};

export type MenuItem = {
  label: string;
  id: string;
  icon: any;
  fields?: FormItem<any>[] | null;
  isEligible?: boolean;
  messageTemplate?: (
    count: number,
    totalCount?: number,
  ) => { baseBold: string; baseNormal: string; details: string };
};

export type BulkEditMenu = {
  tmm_admin: MenuItem[];
  tmm_manager: MenuItem[];
  tmm_technician: MenuItem[];
};

const FIELD_ITEMS = {
  bulk_assign: [
    {
      dataField: 'assignedToId',
      label: 'Assign To',
      editorType: 'RelatedItems',
      isRequired: true,
      validationRules: [{ type: 'required', message: '"Assigned To" is required."' }],
      editorOptions: {
        displayExpr: 'name',
        valueExpr: 'id',
        button: {
          desktopOnly: false,
        },
        dataStoreOptions: {
          getItemList: gql(getUserList),
          getItem: gql(getUser),
        },
      },
    },
    {
      dataField: 'description',
      label: 'Description',
      editorType: 'TextArea',
    },
  ],

  move_to_in_progress: [
    {
      dataField: 'assignedToId',
      label: 'Assign To',
      editorType: 'RelatedItems',
      isRequired: true,
      validationRules: [{ type: 'required', message: '"Assigned To" is required."' }],
      editorOptions: {
        displayExpr: 'name',
        valueExpr: 'id',
        button: {
          desktopOnly: false,
        },
        dataStoreOptions: {
          getItemList: gql(getUserList),
          getItem: gql(getUser),
        },
      },
    },
  ],

  close_work_orders: [
    {
      dataField: 'closedAt',
      label: 'Closed At',
      editorType: 'DateBox',
      isRequired: true,
      validationRules: [{ type: 'required', message: '"Closed At" is required."' }],
      editorOptions: {
        type: 'datetime',
        pickerType: 'calendar',
        value: new Date(),
      },
    },
    {
      dataField: 'closedNotes',
      label: 'Closing Notes',
      editorType: 'TextArea',
      isRequired: true,
      validationRules: [{ type: 'required', message: '"Closing Notes" is required."' }],
    },
  ],

  assign_to_me: [],

  move_to_pending_review: [
    {
      isRequired: true,
      validationRules: [{ type: 'required', message: '"Start Date" is required."' }],
      dataField: 'startDate',
      label: 'Start Date',
      editorType: 'DateBox',
      editorOptions: {
        type: 'datetime',
      },
    },
    {
      isRequired: true,
      validationRules: [{ type: 'required', message: '"End Date" is required."' }],
      dataField: 'endDate',
      label: 'End Date',
      editorType: 'DateBox',
      editorOptions: {
        type: 'datetime',
      },
    },
    {
      dataField: 'actualEffort',
      label: 'Actual Effort (hours)',
      editorType: 'NumberBox',
      isRequired: true,
      validationRules: [{ type: 'required', message: '"Actual Effort" is required."' }],
      editorOptions: {
        format: '#0.#',
        value: 0,
        min: 0,
        step: 0.1,
        showSpinButtons: true,
      },
    },
    {
      dataField: 'closedNotes',
      label: 'Closing Notes',
      editorType: 'TextArea',
      isRequired: true,
      validationRules: [{ type: 'required', message: '"Closing Notes" is required."' }],
    },
  ],
} as { [key: string]: FormItem<any>[] };

const getFields = (key: string) => {
  const fields: FormItem<any>[] = [
    {
      cssClass: 'tmm-section tmm-section-no-reset-margin-padding',
      colCount: {
        xs: 1,
        sm: 1,
        md: 1,
        lg: 1,
      },
      items: FIELD_ITEMS?.[key] ?? [],
    },
  ];

  return fields;
};

const DELETE = {
  label: 'Delete',
  id: 'delete',
  icon: 'trash',
  fields: null,
};

const ASSIGN_TO_ME = {
  label: 'Assign to Me',
  id: 'assign_to_me',
  icon: 'user',
  fields: getFields('assign_to_me'),
  messageTemplate: (count: number, totalCount?: number) => ({
    baseBold: `${count} out of ${totalCount} selected work orders will be affected. `,
    baseNormal: `All of the changes made here will override the data in the original records.`,
    details:
      'To assign a work order it must not be in state "Closed". Only work orders matching the criteria will be affected.',
  }),
};

const BULK_ASSIGN = {
  label: 'Bulk Assign',
  id: 'bulk_assign',
  icon: 'user',
  fields: getFields('bulk_assign'),
  messageTemplate: (count: number, totalCount?: number) => ({
    baseBold: `${count} out of ${totalCount} selected work orders will be affected. `,
    baseNormal: `All of the changes made here will override the data in the original records.`,
    details:
      'To assign a work order it must not be in state "Closed". Only work orders matching the criteria will be affected.',
  }),
};

const MOVE_TO_IN_PROGRESS = {
  label: 'Move to In Progress',
  id: 'move_to_in_progress',
  icon: 'square-arrow-right',
  fields: getFields('move_to_in_progress'),
  messageTemplate: (count: number, totalCount?: number) => ({
    baseBold: `${count} out of ${totalCount} selected work orders will be affected. `,
    baseNormal: `All of the changes made here will override the data in the original records.`,
    details:
      'To move a work order to "In Progress" it has to be in state "Open". Only work orders matching the criteria will be affected.',
  }),
};

const MOVE_TO_PENDING_REVIEW = {
  label: 'Move to Pending Review',
  id: 'move_to_pending_review',
  icon: 'square-arrow-right',
  fields: getFields('move_to_pending_review'),
  messageTemplate: (count: number, totalCount?: number) => ({
    baseBold: `${count} out of ${totalCount} selected work orders will be affected. `,
    baseNormal: `All of the changes made here will override the data in the original records.`,
    details:
      'To move a work order to "Pending Review" it has to be in state “In Progress”. Only work orders matching the criteria will be affected.',
  }),
};

const CLOSE_WORK_ORDERS = {
  label: 'Close Work Orders',
  id: 'close_work_orders',
  icon: 'memo-circle-check',
  fields: getFields('close_work_orders'),
  messageTemplate: (count: number, totalCount?: number) => ({
    baseBold: `${count} out of ${totalCount} selected work orders will be affected. `,
    baseNormal: `All of the changes made here will override the data in the original records.`,
    details:
      'To close a work order it has to be in state "Pending Review". Only work orders matching the criteria will be affected.',
  }),
};

export const BULK_EDIT_MENU_OPTIONS: BulkEditMenu = {
  tmm_admin: [
    ASSIGN_TO_ME,
    BULK_ASSIGN,
    MOVE_TO_IN_PROGRESS,
    MOVE_TO_PENDING_REVIEW,
    CLOSE_WORK_ORDERS,
  ],
  tmm_manager: [BULK_ASSIGN, MOVE_TO_IN_PROGRESS, CLOSE_WORK_ORDERS],
  tmm_technician: [ASSIGN_TO_ME, MOVE_TO_IN_PROGRESS, MOVE_TO_PENDING_REVIEW],
};

export const BULK_EDIT_MENU_OPTIONS_MOBILE: BulkEditMenu = {
  tmm_admin: [
    ASSIGN_TO_ME,
    BULK_ASSIGN,
    MOVE_TO_IN_PROGRESS,
    MOVE_TO_PENDING_REVIEW,
    CLOSE_WORK_ORDERS,
    DELETE,
  ],
  tmm_manager: [BULK_ASSIGN, MOVE_TO_IN_PROGRESS, CLOSE_WORK_ORDERS, DELETE],
  tmm_technician: [ASSIGN_TO_ME, MOVE_TO_IN_PROGRESS, MOVE_TO_PENDING_REVIEW, DELETE],
};
